import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Users, Maximize, BedDouble } from "lucide-react";
import api from "@/lib/api";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Loader } from "@/components/shared/Loader";
import { InquiryDialog } from "@/components/public/InquiryDialog";

export default function PropertyDetail() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [dialog, setDialog] = useState({ open: false, room: null, offer: null });

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setProperty(r.data)).catch(() => {});
  }, [id]);

  if (!property) return <PublicLayout><Loader /></PublicLayout>;

  const openInquiry = (room = null, offer = null) => setDialog({ open: true, room, offer });

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px] w-full overflow-hidden">
        <img src={property.hero_image} alt={property.name} className="kenburns absolute inset-0 h-full w-full object-cover" />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-16 lg:px-10">
          <div className="fade-up mb-3 flex items-center gap-2 text-[var(--aur-text-2)]">
            <MapPin size={14} className="text-gold" />
            <span className="font-mono text-[11px] uppercase tracking-widest">{property.city}, {property.country}</span>
          </div>
          <h1 className="fade-up font-serif text-4xl tracking-tight sm:text-5xl lg:text-6xl">{property.name}</h1>
          <p className="fade-up mt-4 max-w-xl font-sans text-lg text-[var(--aur-text-2)]">{property.tagline}</p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        {/* Story + amenities */}
        <section className="grid gap-12 py-20 md:grid-cols-3">
          <div className="md:col-span-2">
            <p className="eyebrow">The Estate</p>
            <p className="mt-6 font-sans text-lg leading-relaxed text-[var(--aur-text-2)]">{property.description}</p>
          </div>
          <div>
            <p className="eyebrow">Signature Amenities</p>
            <ul className="mt-6 space-y-3">
              {property.amenities.map((a) => (
                <li key={a} className="flex items-center gap-3 font-sans text-sm text-[var(--aur-text)]">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold" />{a}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Gallery */}
        {property.gallery?.length > 0 && (
          <section className="grid gap-4 pb-20 md:grid-cols-2">
            {property.gallery.map((g, i) => (
              <div key={i} className="aspect-[16/10] overflow-hidden rounded-lg">
                <img src={g} alt="" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
              </div>
            ))}
          </section>
        )}

        {/* Rooms */}
        <section className="pb-20">
          <p className="eyebrow">Accommodation</p>
          <h2 className="mt-4 font-serif text-3xl tracking-tight lg:text-4xl">The Room Collection</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {property.rooms?.map((room) => (
              <div key={room.id} data-testid={`room-card-${room.id}`} className="group overflow-hidden rounded-lg border border-[var(--aur-border)] bg-[var(--aur-card)]">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={room.image} alt={room.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl">{room.name}</h3>
                  <div className="mt-4 flex flex-wrap gap-4 font-sans text-xs text-[var(--aur-text-2)]">
                    <span className="flex items-center gap-1.5"><Maximize size={13} className="text-gold" />{room.size}</span>
                    <span className="flex items-center gap-1.5"><BedDouble size={13} className="text-gold" />{room.beds}</span>
                    <span className="flex items-center gap-1.5"><Users size={13} className="text-gold" />{room.occupancy}</span>
                  </div>
                  <div className="mt-5 flex items-center justify-between">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--aur-text-2)]">From</p>
                      <p className="font-serif text-lg text-gold">{room.price_from}<span className="font-sans text-xs text-[var(--aur-text-2)]"> / night</span></p>
                    </div>
                    <button
                      onClick={() => openInquiry(room)}
                      data-testid={`room-request-${room.id}`}
                      className="rounded-full border border-gold/40 px-4 py-2 font-sans text-[10px] font-semibold uppercase tracking-widest text-gold transition-all hover:bg-gold hover:text-[var(--aur-bg)]"
                    >
                      Request
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Offers */}
        {property.offers?.length > 0 && (
          <section className="pb-24">
            <p className="eyebrow">Curated Offers</p>
            <h2 className="mt-4 font-serif text-3xl tracking-tight lg:text-4xl">Reasons to linger</h2>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {property.offers.map((offer) => (
                <div key={offer.id} data-testid={`offer-card-${offer.id}`} className="group relative overflow-hidden rounded-lg border border-[var(--aur-border)]">
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={offer.image} alt={offer.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="absolute inset-0 hero-overlay" />
                  <div className="absolute bottom-0 p-7">
                    <h3 className="font-serif text-2xl">{offer.title}</h3>
                    <p className="mt-2 max-w-md font-sans text-sm text-[var(--aur-text-2)]">{offer.description}</p>
                    <button
                      onClick={() => openInquiry(null, offer)}
                      className="mt-5 rounded-full bg-gold px-5 py-2 font-sans text-[10px] font-semibold uppercase tracking-widest text-[var(--aur-bg)] transition-all hover:bg-gold-bright"
                    >
                      Request this offer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Facilities */}
      {property.facilities?.length > 0 && (
        <div className="border-t border-[var(--aur-border)] bg-[var(--aur-bg-2)]">
          <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
            <p className="eyebrow">Facilities & Experiences</p>
            <h2 className="mt-4 font-serif text-3xl tracking-tight lg:text-4xl">Everything within reach</h2>
            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {property.facilities.map((fac, i) => (
                <div key={i} data-testid={`facility-${i}`} className="group overflow-hidden rounded-lg border border-[var(--aur-border)] bg-[var(--aur-card)]">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={fac.image} alt={fac.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-gold">{fac.category}</p>
                    <h3 className="mt-2 font-serif text-xl">{fac.name}</h3>
                    <p className="mt-2 font-sans text-sm leading-relaxed text-[var(--aur-text-2)]">{fac.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sticky booking bar */}
      <div className="sticky bottom-0 z-30 glass border-t border-[var(--aur-border)]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <div>
            <p className="font-serif text-lg">{property.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--aur-text-2)]">Manual confirmation · No payment now</p>
          </div>
          <button
            onClick={() => openInquiry()}
            data-testid="btn-request-to-book"
            className="rounded-full bg-gold px-7 py-3 font-sans text-xs font-semibold uppercase tracking-widest text-[var(--aur-bg)] transition-all hover:bg-gold-bright"
          >
            Request to Book
          </button>
        </div>
      </div>

      <InquiryDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        property={property}
        room={dialog.room}
        offer={dialog.offer}
      />
    </PublicLayout>
  );
}
